import { useMemo } from 'react';

import {
  GoogleChatSpaceType,
  GoogleIntegrationStatus,
} from '@this+that/models';
import log from 'loglevel';

import { useIntegrationsStore } from '../store';
import { createApiWithAuth } from '../utils/axios';

export type { GoogleChatSpaceType };
export type GoogleChatSpaceTypeFilter = GoogleChatSpaceType | 'ALL';

let googleChatApi: ReturnType<typeof createApiWithAuth> | null = null;

const getGoogleChatApi = () => {
  if (!googleChatApi) {
    const baseUrl = import.meta.env.VITE_GOOGLE_BASE_URL;
    if (!baseUrl) {
      throw new Error('Google base URL is not set');
    }
    googleChatApi = createApiWithAuth(baseUrl);
  }
  return googleChatApi;
};

export interface GoogleChatSpaceResult {
  name: string;
  displayName: string;
  spaceType: GoogleChatSpaceType;
  memberCount: number;
}

export interface GoogleChatMemberResult {
  memberId: string;
  displayName: string;
  email: string;
  avatarUrl?: string;
  isBot: boolean;
}

export interface GetGoogleChatSpacesResponse {
  spaces: GoogleChatSpaceResult[];
}

export interface GetGoogleChatSpaceMembersResponse {
  members: GoogleChatMemberResult[];
}

export interface GoogleChatReplyResult {
  name?: string;
  thread?: string;
  createTime?: string;
}

export interface GoogleChatReplyBody {
  emailAddress: string;
  spaceName: string;
  text: string;
  threadName?: string;
  requestId: string;
}

const CHAT_REPLY_PATH = '/chat/reply';
const CHAT_SPACES_PATH = '/chat/spaces';
const CHAT_MEMBERS_PATH = '/chat/members';

export const getGoogleChatSpaces = async (
  emailAddress: string,
  spaceType?: GoogleChatSpaceTypeFilter,
): Promise<GetGoogleChatSpacesResponse> => {
  try {
    const { data } = await getGoogleChatApi().get(CHAT_SPACES_PATH, {
      params: {
        emailAddress,
        ...(spaceType !== undefined && { spaceType }),
      },
    });
    return data.message;
  } catch (error) {
    log.error('Error fetching Google Chat spaces:', error);
    throw error;
  }
};

export const getGoogleChatMembers = async (
  emailAddress: string,
): Promise<GetGoogleChatSpaceMembersResponse> => {
  try {
    const { data } = await getGoogleChatApi().get(CHAT_MEMBERS_PATH, {
      params: { emailAddress },
    });
    return data.message;
  } catch (error) {
    log.error('Error fetching Google Chat members:', error);
    throw error;
  }
};

export const sendGoogleChatReply = async (
  body: GoogleChatReplyBody,
): Promise<GoogleChatReplyResult> => {
  try {
    const { threadName, ...rest } = body;
    const { data } = await getGoogleChatApi().post(CHAT_REPLY_PATH, {
      ...rest,
      ...(threadName !== undefined && { threadName }),
    });
    return data.message;
  } catch (error) {
    log.error('Error sending Google Chat reply:', error);
    throw error;
  }
};

const hasChatSubscription = (item: GoogleIntegrationStatus): boolean => {
  return item.chatEnabled === true;
};

export function useGetUserGoogleChatAccounts() {
  const userGoogleIntegrations = useIntegrationsStore(
    (s) => s.userGoogleIntegrations,
  );
  const isLoading = useIntegrationsStore((s) => s.isLoading);

  const googleChatAccounts = useMemo(
    () => userGoogleIntegrations.filter(hasChatSubscription),
    [userGoogleIntegrations],
  );

  return { googleChatAccounts, loading: isLoading };
}
